/** The render half of one canvas, drawn the way this canvas's layout says. */

import {
  CanvasView,
  cloudSurfaceCatalogue,
  type EntityIndex,
  type PickedNode,
} from "@nebulize/overlay/render";
import type { CanvasArrangement, CueFrame, RegionMap, SceneDocument } from "@nebulize/sdk";
import { useMemo } from "react";

import { apiBaseUrl } from "@/lib/env";

import { COMPOSITED, pictureScenes, type CanvasLayout } from "./composition";

import "./canvas.css";

/** Surface modules come off the api, which is not this page's origin in every deployment. */
const SURFACES = cloudSurfaceCatalogue(apiBaseUrl);

export interface CanvasPictureProps {
  readonly layout: CanvasLayout;
  readonly canvasKind: string;
  readonly scenes: readonly SceneDocument[];
  readonly entities: EntityIndex;
  readonly regions: readonly RegionMap[];
  /** The whole space's arrangement. Absent leaves the compositor's own default rule standing. */
  readonly arrangement?: CanvasArrangement;
  readonly stale?: boolean;
  /** The last cue this host's transport carried. The render half plays each envelope once. */
  readonly cue?: CueFrame;
  readonly onPick?: (picked: PickedNode) => void;
}

export function CanvasPicture({
  layout,
  canvasKind,
  scenes,
  entities,
  regions,
  arrangement,
  stale = false,
  cue,
  onPick,
}: CanvasPictureProps) {
  const documents = useMemo(() => pictureScenes(scenes, layout), [scenes, layout]);

  const state = useMemo(
    () => ({
      scenes: documents,
      entities,
      regions,
      stale,
      ...(arrangement === undefined ? {} : { arrangement }),
    }),
    [documents, entities, regions, stale, arrangement],
  );

  const view = (
    <CanvasView
      canvasKind={canvasKind}
      state={state}
      surfaces={SURFACES}
      {...(cue === undefined ? {} : { cue })}
      {...(onPick === undefined ? {} : { onPick })}
    />
  );

  // The composite fills the positioned stage it was given, and stays mounted with nothing to draw.
  if (layout === COMPOSITED) return view;

  if (documents.length === 0) return null;
  return <div className="al-canvas-picture">{view}</div>;
}
